'use client';

import { c as _c } from "react/compiler-runtime";
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useUAContext } from '@/context/UAContext';
import { useEnableFull3d } from '@/hooks/useEnableFull3d';
import { useMobileBrowserDeviceCapability } from '@/hooks/useMobileBrowserDeviceCapability';
import { useNativeDeviceCapability } from '@/hooks/useNativeDeviceCapability';
import { enableMobileBrowserGating } from '@/lib/configs';
import { normalizeContentTier, resolveContentDisplayState, type ContentDisplayState } from '@/lib/cross-platform-gating';
import { isIPadOSDesktopBrowser } from '@/lib/ipados';
import { useSearchParams } from 'next/navigation';
import { useLayoutEffect, useState } from 'react';
const CROSS_PLATFORM_GATING_DEBUG = 'gating-debug' as const;
type CrossPlatformGating = {
  displayState: ContentDisplayState;
  isIPadReclassified: boolean;
  shouldShowCompatibilityTrailer: boolean;
  shouldShowCompatibilityNotice: boolean;
};
export function useCrossPlatformGatingInput() {
  const $ = _c(5);
  const searchParams = useSearchParams();
  const debug = searchParams.has(CROSS_PLATFORM_GATING_DEBUG);
  const {
    roomData
  } = useCheckEnterRoomContext();
  const {
    device
  } = useUAContext();
  const enableFull3d = useEnableFull3d();
  const {
    capabilityTier: nativeTier
  } = useNativeDeviceCapability();
  const mobileBrowserGatingEnabled = enableMobileBrowserGating();
  const [effectiveDevice, setEffectiveDevice] = useState(device);
  const [isIPadReclassified, setIsIPadReclassified] = useState(false);
  useLayoutEffect(() => {
    if (!mobileBrowserGatingEnabled) {
      setEffectiveDevice(device);
      setIsIPadReclassified(false);
      return;
    }
    const isIPadOSDesktop = device === "desktop_browser" && isIPadOSDesktopBrowser(navigator.userAgent, navigator.maxTouchPoints);
    setEffectiveDevice(isIPadOSDesktop ? "mobile_browser" : device);
    setIsIPadReclassified(isIPadOSDesktop);
  }, [device, mobileBrowserGatingEnabled]);
  const {
    status: browserCapabilityStatus,
    capabilityTier: browserTier,
    consumerBucket
  } = useMobileBrowserDeviceCapability({
    enabled: mobileBrowserGatingEnabled,
    device: effectiveDevice
  });
  const capabilityTier = effectiveDevice === "mobile_app" ? nativeTier : browserCapabilityStatus === "ready" ? browserTier : "unknown";
  const input = {
    sceneType: roomData.scene_type,
    device: effectiveDevice,
    capabilityTier,
    contentTier: normalizeContentTier(roomData?.performance_tier),
    mobileBrowserGatingEnabled
  };
  const displayState = resolveContentDisplayState(input);
  const shouldShowCompatibilityTrailer = displayState === "Label" && !isIPadReclassified && !enableFull3d;
  const shouldShowCompatibilityNotice = displayState === "Label" && isIPadReclassified && !enableFull3d;
  if (debug) {
    console.info("[Cross Platform Gating]", {
      ...input,
      browserCapabilityStatus,
      consumerBucket,
      serverDevice: device,
      isIPadReclassified
    });
  }
  let t0;
  if ($[0] !== displayState || $[1] !== isIPadReclassified || $[2] !== shouldShowCompatibilityNotice || $[3] !== shouldShowCompatibilityTrailer) {
    t0 = {
      displayState,
      isIPadReclassified,
      shouldShowCompatibilityTrailer,
      shouldShowCompatibilityNotice
    };
    $[0] = displayState;
    $[1] = isIPadReclassified;
    $[2] = shouldShowCompatibilityNotice;
    $[3] = shouldShowCompatibilityTrailer;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}